import Phaser from 'phaser';
import { DragTracker, type TouchSnapshot } from './InputController';
import { ANCHOR_INITIAL_Y, ANCHOR_MAX_Y, ANCHOR_MIN_Y } from './Bar';
import type { Bar } from './Bar';
import { LOGICAL_WIDTH, RENDER_SCALE } from '../dimensions';
import { tokens } from '../design/tokens';

export class InputBindings {
  private tracker: DragTracker;
  private keys: { wsUp: Phaser.Input.Keyboard.Key; wsDown: Phaser.Input.Keyboard.Key; arrUp: Phaser.Input.Keyboard.Key; arrDown: Phaser.Input.Keyboard.Key };
  private enabled = true;

  constructor(scene: Phaser.Scene, private bar: Bar) {
    this.tracker = new DragTracker({
      viewportWidth: LOGICAL_WIDTH,
      anchorMinY: ANCHOR_MIN_Y,
      anchorMaxY: ANCHOR_MAX_Y,
      initialLeftY: ANCHOR_INITIAL_Y,
      initialRightY: ANCHOR_INITIAL_Y,
      dragScale: tokens.input.touch.dragScale,
      maxTiltDeltaPx: tokens.input.touch.maxTiltDeltaPx,
    });

    // Phaser pointer.x/.y are in canvas backing-store space (RENDER_SCALE× the
    // logical viewport). Divide by RENDER_SCALE to get logical screen coords,
    // which are camera-independent. This is the key fix: world-space input
    // couples to camera scroll, screen-space input does not.
    scene.input.on('pointerdown', (p: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      this.tracker.pointerDown(p.id, p.x / RENDER_SCALE, p.y / RENDER_SCALE);
    });
    scene.input.on('pointermove', (p: Phaser.Input.Pointer) => {
      if (!this.enabled) return;
      if (!p.isDown) return;
      const update = this.tracker.pointerMove(p.id, p.x / RENDER_SCALE, p.y / RENDER_SCALE);
      if (update) this.bar.setAnchor(update.zone, update.anchorY);
    });
    // pointerup is intentionally unconditional — releasing tracker state stays
    // consistent even if input was disabled mid-touch.
    scene.input.on('pointerup', (p: Phaser.Input.Pointer) => this.tracker.pointerUp(p.id));
    scene.input.on('pointerupoutside', (p: Phaser.Input.Pointer) => this.tracker.pointerUp(p.id));

    const kb = scene.input.keyboard!;
    this.keys = {
      wsUp:   kb.addKey('W'),
      wsDown: kb.addKey('S'),
      arrUp:  kb.addKey('UP'),
      arrDown:kb.addKey('DOWN')
    };
  }

  public update(dtMs: number): void {
    if (!this.enabled) return;

    // 1) Velocity overlay from any held pointers.
    for (const u of this.tracker.tick(dtMs, tokens.input.joystick)) {
      this.bar.setAnchor(u.zone, u.anchorY);
    }

    // 2) Keyboard.
    const dy = (dtMs / 1000) * tokens.input.keyboard.speedPxPerSec;
    if (this.keys.wsUp.isDown)    this.tickKey('left',  -dy);
    if (this.keys.wsDown.isDown)  this.tickKey('left',   dy);
    if (this.keys.arrUp.isDown)   this.tickKey('right', -dy);
    if (this.keys.arrDown.isDown) this.tickKey('right',  dy);
  }

  public setEnabled(enabled: boolean): void {
    // Releasing pointers when disabling matters more in hybrid mode: a held
    // thumb at offset would otherwise integrate velocity every frame during
    // the disabled window, and on re-enable the bar would have travelled a
    // long way unbidden.
    if (!enabled && this.enabled) this.tracker.releaseAll();
    this.enabled = enabled;
  }

  public reset(): void {
    this.tracker.releaseAll();
    // Use resetAnchors (bypasses tilt clamp) so both anchors land at
    // ANCHOR_INITIAL_Y regardless of the previous level's stale values —
    // otherwise the first per-anchor setAnchor would clamp against the stale
    // other-side anchor and leave the tracker stuck near the old position.
    this.tracker.resetAnchors(ANCHOR_INITIAL_Y, ANCHOR_INITIAL_Y);
    this.enabled = true;
  }

  public getActiveTouchSnapshots(): TouchSnapshot[] {
    if (!this.enabled) return [];
    return this.tracker.getActiveTouchSnapshots();
  }

  private tickKey(zone: 'left' | 'right', delta: number): void {
    const next = this.tracker.getAnchor(zone) + delta;
    this.tracker.setAnchor(zone, next);
    this.bar.setAnchor(zone, this.tracker.getAnchor(zone));
  }
}
